// Entity type definitions for the tower defense game

// ─── Element System ────────────────────────────────────
import { ELEMENTS, type Element } from "./elements.ts";
export { ELEMENTS, type Element };

// ─── Tower ─────────────────────────────────────────────
export interface TowerConfig {
  element: Element;
  damage: number;
  range: number;
  fireRate: number;
  projectileSpeed: number;
  color: string;
  colorLight: string;
  cost: number;
  special?: SpecialEffect | null;
  detectStealth?: boolean;
  /** Per-tower upgrade multipliers (F-12). Missing keys fall back to the
   *  shared UPGRADE_MULTIPLIERS — e.g. Signal sets damage→1 (no-op, it has
   *  none) and invests the level in range/fireRate instead. */
  upgradeProfile?: {
    damage?: number;
    range?: number;
    fireRate?: number;
  };
}

export const TOWER_SPECIAL_TYPES = [
  "dot",
  "chain",
  "burst",
  "mark",
  "railshot",
  "infection",
] as const;

export type TowerSpecialType = (typeof TOWER_SPECIAL_TYPES)[number];

export interface SpecialEffect {
  type: TowerSpecialType;
  // dot
  duration?: number;
  tickDamage?: number;
  tickInterval?: number;
  /** BL-174 / D-263 — fire's "Scorch" capability (unlocked at
   *  `TOWER_CAPABILITY.UNLOCK_LEVEL`): the impact splashes `splashFraction` of
   *  the (post-multiplier) hit damage to every other foe within `splashRadius`
   *  px. This is the wiring of the long-dead `fire.splash` field (BL-179) into a
   *  real, level-gated AoE — anti-swarm at last. Absent ⇒ no splash (byte-safe
   *  for every non-fire tower and for fire below the gate). */
  splashRadius?: number;
  splashFraction?: number;
  /** BL-182 / D-265 (S169) — fire's "Area Burn" arc L4/L5 beats, the higher gates
   *  of the Scorch splash. L4 "Wildfire" (`wildfire`): every splash-hit foe ALSO
   *  catches the DoT (the burn PROPAGATES through the splash). L5 "Firestorm"
   *  (`firestormRadiusMult`/`firestormFractionMult`): the splash widens + hits
   *  harder (radius × / fraction ×). All gated on level in `applyFireSplash`;
   *  absent ⇒ inert (byte-safe for every sub-gate / non-fire shot). */
  wildfire?: boolean;
  firestormRadiusMult?: number;
  firestormFractionMult?: number;
  /** BL-175 / D-266 (S170) — fire's GOLD signature "HELIOS · Combustion". When
   *  the GOLD fire tower lands the KILLING blow, the corpse DETONATES for this
   *  fraction of its max-hp as a fire-AoE in `goldCombustionRadius` px (kills can
   *  chain into kills). A brand-new death-trigger, NOT the Scorch splash arc.
   *  Gated on `tower.isGold()` in `applyFireSplash`; absent / sub-GOLD ⇒ inert. */
  goldCombustionFraction?: number;
  goldCombustionRadius?: number;
  // chain
  bounces?: number;
  damageDecay?: number;
  /** BL-174 / D-263 — water's "Cryo-Chain" capability (unlocked at
   *  `TOWER_CAPABILITY.UNLOCK_LEVEL`): every enemy a chain bounce hits is slowed
   *  to `chainSlowFactor` of its speed for `chainSlowMs`. Gives water a real
   *  late-game control niche (§H2). Absent ⇒ no slow (byte-safe). */
  chainSlowFactor?: number;
  chainSlowMs?: number;
  /** BL-182 / D-265 (S169) — water's "Control / Freeze" arc L4/L5 beats. L4
   *  "Deep-Chill" (`deepChillBounce`): the chain gains this many EXTRA bounces at
   *  the gate (a deeper net). L5 "Flash-Freeze" (`flashFreezeMs`): the PRIMARY
   *  target is fully stopped (slow ×0) for this long on hit. Gated on level in
   *  `chainHit`; absent ⇒ inert (byte-safe for every sub-gate / non-water shot). */
  deepChillBounce?: number;
  flashFreezeMs?: number;
  /** BL-175 / D-266 (S170) — water's GOLD signature "ABSOLUTE ZERO · Cryo-Nova".
   *  On a cadence (`goldNovaPeriodMs`, banked per cast), the GOLD water tower's
   *  hit triggers a board-region STASIS pulse that freezes every foe within
   *  `goldNovaRadius` px solid (slow ×0) for `goldNovaFreezeMs`. A brand-new area
   *  stasis, NOT the chain. Deterministic (a per-tower dt timer, no rng). Gated on
   *  `tower.isGold()` in `chainHit`; absent / sub-GOLD ⇒ inert. */
  goldNovaPeriodMs?: number;
  goldNovaRadius?: number;
  goldNovaFreezeMs?: number;
  // burst
  critChance?: number;
  critMultiplier?: number;
  /** BL-174 / D-263 — lightning's "Overload" capability (unlocked at
   *  `TOWER_CAPABILITY.UNLOCK_LEVEL`): on top of the RNG `critChance`, the beam
   *  banks time on-target and forces a GUARANTEED crit every `surgePeriodMs`,
   *  turning the pure-variance burst into a dependable cadence (§H3).
   *  Deterministic (a dt-accumulating timer, no RNG). Absent ⇒ no surge. */
  surgePeriodMs?: number;
  /** BL-182 / D-265 (S169) — lightning's "Burst → Arc" arc L4/L5 beats, both
   *  riding the deterministic Overload surge (never the RNG crit). L4 "Fork"
   *  (`forkFraction`): a surge crit ALSO forks to the nearest other foe for this
   *  fraction of the dealt damage. L5 "Chain-Storm" (`chainStormRadius`): the
   *  surge crit instead arcs to EVERY foe within this radius (a mini-AoE on the
   *  guaranteed crit). Gated on level in `updateBeamTower`; absent ⇒ inert. */
  forkFraction?: number;
  chainStormRadius?: number;
  /** BL-175 / D-266 (S170) — lightning's GOLD signature "TEMPEST · Thunderstrike".
   *  On a cadence (`goldStrikePeriodMs`, banked per beam frame), the GOLD lightning
   *  tower calls down `goldStrikeCount` sky-bolts onto the HIGHEST-hp foes across
   *  the WHOLE board (off-beam, range-independent), each for `goldStrikeDamage`.
   *  Brand-new off-beam strikes. Deterministic (a dt timer + a value sort, no rng).
   *  Gated on `tower.isGold()` in `updateBeamTower`; absent / sub-GOLD ⇒ inert. */
  goldStrikePeriodMs?: number;
  goldStrikeCount?: number;
  goldStrikeDamage?: number;
  // mark (signal tower)
  markDamageBonus?: number;
  slowFactor?: number;
  markDuration?: number;
  /** BL-175 / D-266 (S170) — signal's GOLD signature "ORACLE · Command Aura". The
   *  GOLD signal tower becomes a force-multiplier: every FRIENDLY tower within
   *  `goldAuraRadius` px deals `goldAuraDamageMult`× damage, and all stealth on the
   *  board is revealed. Brand-new EMPOWER (buffs allies, not enemies). Applied as a
   *  per-frame transient stamp on each buffed tower, read at its damage chokepoint;
   *  gated on `tower.isGold()`; absent / sub-GOLD ⇒ no aura. */
  goldAuraRadius?: number;
  goldAuraDamageMult?: number;
  // railshot (kinetic tower)
  chargeTime?: number;
  aimTime?: number;
  recoverTime?: number;
  snapShotMultiplier?: number;
  /** BL-175 / D-266 (S170) — kinetic's GOLD signature "ANNIHILATOR · Orbital
   *  Round". The GOLD kinetic's charged shot is an EXECUTE: it one-shots any
   *  NON-boss foe in the lane outright, and lands `goldExecuteBossFraction` of
   *  max-hp on a boss/tank. Brand-new execute payload. Deterministic. Gated on
   *  `tower.isGold()` in `resolveShot`; absent / sub-GOLD ⇒ a normal shot. */
  goldExecuteBossFraction?: number;
  // infection (virus tower)
  zoneRadius?: number;
  spreadRadius?: number;
  spreadChance?: number;
  maxGenerations?: number;
  spreadInterval?: number;
  speedDebuff?: number;
  /** BL-175 / D-266 (S170) — virus's GOLD signature "PANDEMIC · Patient Zero". An
   *  INFECTED foe that dies bursts a spore cloud that re-infects every foe within
   *  `goldSporeRadius` px (the plague becomes self-sustaining across the lane).
   *  Brand-new death-propagation. Deterministic. Gated on `tower.isGold()` at the
   *  infection seed; absent / sub-GOLD ⇒ no spore burst. */
  goldSporeRadius?: number;
}

// ─── Enemy ─────────────────────────────────────────────
export interface EnemyConfig {
  name: string;
  shortName?: string;
  hp: number;
  speed: number;
  reward: number;
  color: string;
  radius: number;
  /** BL-51: per-archetype procedural silhouette family (dart/block/cluster/
   *  worm/glyph/ram/boss/…). Render-only; the sim never reads it. Missing →
   *  the diamond fallback. See `EnemyShapes.ts` for the registered families. */
  shape?: string;
  weakness?: Element | null;
  resistance?: Element | null;
  special?: EnemySpecial | null;
  livesDamage?: number;
  stealth?: boolean;
  firewallDamage?: number;
  immunities?: string[];
  /**
   * GA-07 / GA-19 (Spec C+) — the authored combat ARCHETYPE, and the single
   * source of truth for {@link isTanky}.
   *
   * `isTanky` used to compare `enemy.type` against the literals `"boss"`,
   * `"final_boss"` and `"golem"`. But `Enemy.init` sets `this.type =
   * config.name` — the DISPLAY name — so those comparisons ran against
   * `"Core Breaker"`, `"AXIOM-77 Core"` and `"Rootkit"` and could never match.
   * Verified by execution across all 19 shipped types: the predicate returned
   * `false` for every one of them, bosses included.
   *
   * That silently disabled four production paths: the `zero_day_exploit` /
   * `cold_boot` boss-damage multiplier on projectile, sniper AND beam damage,
   * and GOLD kinetic's `goldExecuteBossFraction` execute — an entire L6
   * pinnacle mechanic that never once fired.
   *
   * Authoring it as data (rather than fixing the string list in code) is the
   * repo's own SSOT rule, and makes the next enemy addition declare its
   * archetype instead of silently inheriting the wrong one.
   */
  archetype?: EnemyArchetype;
}

/**
 * GA-07 / GA-19 — the combat archetype roster.
 *
 * `tanky` is the one the sim branches on today (see {@link EnemyConfig.archetype}).
 * `standard` is the explicit default, present so every enemy DECLARES its
 * archetype rather than being classified by omission — which is exactly how the
 * original hardcoded list fell out of date.
 */
export type EnemyArchetype = "standard" | "tanky";

export interface EnemySpecial {
  type: EnemySpecialType;
  interval?: number;
  addType?: string;
  spawnCount?: number;
  spawnThreshold?: number;
  phases?: number;
  phaseThresholds?: number[];
  spawnAddsPerPhase?: string[];
  /** BL-152 (S154) — the Jammer's tower-suppression reach in pixels. While the
   *  Jammer lives, every tower within this radius runs on "slow-time" (a reduced
   *  fire-rate / DPS, the JAM block in config). Deterministic (no `enemyRng`). */
  jamRadius?: number;
  /** BL-152 (S154) — the Overseer's aura reach in pixels. While the host lives,
   *  every NON-overseer enemy within takes reduced damage (the AURA block in
   *  config); the buff collapses when the host dies. Deterministic. */
  auraRadius?: number;
  /** S156 / PT-41 — the Jammer's suppression STRENGTH, lifted out of the
   *  `config.ts` `JAM` constant into this central data row (one archetype, one
   *  tunable place). The jammed tower's per-frame dt is scaled by this (0.45 ⇒
   *  ~45% fire-rate/DPS). Absent → falls back to `JAM.FIRE_RATE_MULT` (byte-safe). */
  fireRateMult?: number;
  /** S156 / PT-41 — the Overseer aura's mitigation STRENGTH, lifted out of the
   *  `config.ts` `AURA` constant. A buffed enemy takes this × incoming damage
   *  (0.5 ⇒ −50%). Absent → falls back to `AURA.DAMAGE_TAKEN_MULT` (byte-safe). */
  damageTakenMult?: number;
  /** S156 / PT-41 — the suppression/aura stamp LIFETIME without a refresh (ms),
   *  lifted out of `JAM.WINDOW_MS` / `AURA.WINDOW_MS`. Absent → the matching
   *  config default (byte-safe). One data row now carries an archetype's whole
   *  effect tuning (radius + strength + window). */
  windowMs?: number;
  /** BL-156 / D-254 (S157) — the ENEMY BRAIN roster flag. When `true`, this
   *  archetype fires the once-per-run "NEW THREAT DETECTED" telegraph on its
   *  debut (the `EnemyDirector` reads it; the introduce-at-wave-N cadence). This
   *  is the DATA-driven home of what was the hardcoded `NEW_THREAT_TYPES`
   *  allowlist — adding a telegraphed threat is now a one-line `enemies.json`
   *  edit. Carried by all five threat-pack specials (jammer, overseer, leech,
   *  regenerator, splitter). BL-170 (S163) ALSO keys the ECHELON mutation
   *  exemption off this flag (`WaveManager.applyModifier`): a `newThreat` enemy's
   *  identity IS its special, so it must not be mutated into a `splitting`/`veiled`
   *  double-threat — one flag now marks both "telegraph me" and "don't mutate me". */
  newThreat?: boolean;
  /** BL-155 (S158) — the LEECH's per-tick credit siphon (the `enemy.leech` emit
   *  the EconomySystem subtracts). Drains `drainAmount` credits every `interval`
   *  ms (reusing `interval`), bounded to `drainCap` total per instance so it can
   *  never bankrupt you unfairly. Deterministic (a timer, no `enemyRng`). */
  drainAmount?: number;
  /** BL-155 (S158) — the LEECH's per-instance lifetime drain cap (credits). */
  drainCap?: number;
  /** BL-155 (S158) — the REGENERATOR's heal rate as a fraction of `maxHp` PER
   *  SECOND (shield-then-HP, bounded to the maxima). 0.09 ⇒ ~9 %/s → immortal
   *  under low DPS, killable with burst/focus. Deterministic (a per-tick heal). */
  regenFraction?: number;
}

export const ENEMY_SPECIAL_TYPES = [
  "shiftResistance",
  "spawnAdds",
  "finalBoss",
  // BL-152 (S154) — the two per-act "new threats" on the BL-151 behavior seam.
  "jammer", // Act II — suppresses nearby TOWERS (the first enemy that attacks your defense)
  "overseer", // Act III — force-multiplies nearby ENEMIES (aura collapses on host death)
  // BL-155 (S158) — Threat Pack 2: three new strategic axes on the same seam.
  "leech", // Act II — siphons your CREDITS (punishes hoarding; ties to the S156 economy)
  "regenerator", // Act III — heals ~9 %/s shield-then-HP (punishes low DPS; forces burst)
  "splitter", // Act II/III — death → 4 fast sub-enemies (punishes single-target; forces AoE)
] as const;

export type EnemySpecialType = (typeof ENEMY_SPECIAL_TYPES)[number];

// ─── Data file schemas ─────────────────────────────────
export interface TowerDataFile {
  [key: string]: TowerConfig;
}

export interface EnemyDataFile {
  [key: string]: EnemyConfig;
}

export interface WaveGroup {
  type: string;
  count: number;
  delay: number;
  groupDelay?: number;
}

export interface WaveTemplate {
  wave: number;
  groups: WaveGroup[];
  preDelay?: number;
}

export interface HpScalingPhase {
  upToWave: number;
  multiplier: number;
}

/**
 * S156 / PT-41 / D-253 — the OPTIONAL central economy-tuning block (the
 * modularity lift: the kill-gold flood + the surplus cap re-tune is a one-line
 * DATA edit, not a `EconomySystem.ts` rebuild). All fields optional; an absent
 * block (or any absent field) resolves to the `DEFAULT_ECONOMY_CONFIG` code
 * default — and that default's `killGoldRamp` is the IDENTITY (×1.0), so a
 * missing block is byte-identical to the pre-S156 economy. Resolved via
 * `resolveEconomyConfig` (EconomySystem.ts).
 */
export interface EconomyTuning {
  /** Banked gold above which the per-wave BONUS starts softening (Act II+). */
  surplusThreshold?: number;
  /** Width of the softening ramp (gold) from full → floor. */
  surplusRange?: number;
  /** Floor the BONUS softens to for a hard-banking player. */
  surplusFloor?: number;
  /** First wave the surplus BONUS cap applies (Act I income is sacred). */
  capStartWave?: number;
  /** D-322 / AC-5 (Task-007) — the flat per-wave-clear bonus formula's base
   *  and per-wave slope (`base + wave * perWave`, pre-S-4/late-slope/surplus).
   *  Absent → the byte-safe code default (30 / 5, `EconomySystem.ts`). */
  waveBonus?: {
    /** Flat base of the wave-clear bonus (pre-S-4 literal: 30). */
    base?: number;
    /** Per-wave slope of the wave-clear bonus (pre-S-4 literal: 5). */
    perWave?: number;
    /** Spec B S-4 — the wave-clear bonus LATE SLOPE: from `lateStartWave`, each
     *  wave adds `(w - lateStartWave + 1) * latePerWave` on top of the flat
     *  `base + perWave*w` base, BEFORE the surplus softener (D-236's banking
     *  guard binds unchanged). Absent → identity (byte-identical pre-S-4
     *  bonus). Exists because the checkpoint measured the base ladder flat
     *  (+95%) against ~×23 volume growth across W14-35. */
    lateStartWave?: number;
    latePerWave?: number;
  };
  /** D-322 / AC-5 (Task-007) — the sell refund ratio (`EconomySystem.SELL_MULTIPLIER`).
   *  Absent → the byte-safe code default (0.7). */
  sellMultiplier?: number;
  /**
   * The "Bloons" per-wave KILL-GOLD ramp — the systemic anti-runaway. The
   * kill reward is multiplied by a factor that is full early and DECAYS late,
   * so the player can't out-farm the threat (throttles the flood at the
   * source, stronger than only softening the wave bonus).
   */
  killGoldRamp?: {
    /** Waves ≤ this earn full kill-gold (×1.0) — W1-5 stays FUN + byte-safe. */
    fullUntilWave?: number;
    /** The multiplier floor reached at (and held past) `floorWave`. */
    floor?: number;
    /** Wave at which the ramp reaches `floor` (linear between). */
    floorWave?: number;
  };
}

export interface WaveDataFile {
  templates: WaveTemplate[];
  scaling: {
    hpMultiplierPhases?: HpScalingPhase[];
    hpMultiplierPerWave?: number;
    speedMultiplier?: number;
    speedMultiplierPerWave?: number;
    countBonus?: number;
    countBonusPerWave?: number;
    /**
     * S-8 (Spec B, Act-I correctness slice) — the boss-group lives-damage
     * guard. `countBonusPerWave` multiplies EVERY group including `boss`
     * with no exemption, so a late-authored/Echo-Mode wave can scale the
     * boss count until its `livesDamage` total exceeds the entire life
     * budget in one wave (measured: W15 delivered 6 × 5 = 30, 120% of
     * `GAME.INITIAL_HP`; Echo Mode unboundedly worse — 11 at W36, 13 at
     * W41). This fraction caps the SCALED boss-type group count so its
     * total `livesDamage` never exceeds `fraction * GAME.INITIAL_HP`.
     * Defaults to 1 (100%) if absent. Covers both the authored arc and Echo
     * Mode (`WaveManager.buildSpawnQueue` runs for both — Echo Mode has no
     * separate scaling path). Boss-only: never applied to non-boss groups.
     */
    bossLivesDamageCapFraction?: number;
    /**
     * AC-10 / O-5 branch (c), Spec B — the declared share of a wave's
     * non-boss spawns that emits from a BREACH lane *instead of* the
     * primary, once at least one breach has erupted.
     *
     * Before this knob a breach DUPLICATED the wave: `spawnEnemy` acquired
     * one enemy per registered path entry, so the entire non-boss
     * composition was mirrored onto every breach lane, permanently
     * (`BreachSystem`: "spawns never deactivate"). That is a pure volume
     * multiplier and the opposite of `CHALLENGE_DESIGN.md:230-247`, which
     * designed a *fraction* of a wave's groups to emit from the off-wall
     * point as "a flanking sub-group that joins the path closer to the
     * core".
     *
     * Routing re-assigns which lane a spawn emits from; it never changes
     * HOW MANY enemies a wave delivers. The per-wave spawn count — and
     * therefore delivered Σ-HP — is invariant under this knob at every
     * value, which is what AC-10's paired "delivered Σ-HP did not fall"
     * assertion requires (a routing lever that shed volume would make the
     * game easier while claiming to harden it).
     *
     * 0 (the default when absent) reproduces the mirroring behaviour
     * exactly, byte-for-byte, so the knob is a true no-op until tuned.
     */
    breachRoutedSpawnFraction?: number;
  };
  /** S156 / D-253 — the optional central economy-tuning block (see above). */
  economy?: EconomyTuning;
  /** D-322 / AC-5 (Task-007) — the optional shield tuning block
   *  (`core/config.ts` `SHIELD` / `SHIELD_START_WAVE`). Absent/partial →
   *  the byte-safe code defaults for the missing fields. */
  shield?: {
    startWave?: number;
    CHANCE_MIN?: number;
    CHANCE_MAX?: number;
    HP_FRAC_MIN?: number;
    HP_FRAC_MAX?: number;
    EMP_MULT?: number;
  };
  /** D-322 / AC-5 (Task-007) — the optional combo tuning block
   *  (`systems/ScoreSystem.ts`). Absent/partial → the byte-safe code
   *  defaults for the missing fields. */
  score?: {
    comboTimeoutMs?: number;
    comboThresholds?: number[];
    comboMultipliers?: number[];
  };
}

export interface SynergyEffect {
  type:
    | "aoeSlowDamage"
    | "beamDamage"
    | "stunOnHit"
    | "statBoostAura"
    | "aoe_pulse"
    | "autoTargetMarked"
    | "aoeMark"
    | "markedDotBoost" // CR-H8: Signal+Fire "Burning Mark"
    | "chargeReduction" // S37c: Sniper+Signal designated marksman
    | "chainOnKill" // S37c: Sniper+Lightning railarc
    // B2 (S41): Virus cross-element synergies
    | "dualDot" // virus+fire wildfire
    | "enhancedSpread" // virus+water waterborne
    | "unlimitedSpreadOnMarked"; // virus+signal plague
  aoeRadius?: number;
  damage?: number;
  slowFactor?: number;
  slowDuration?: number;
  radius?: number;
  tickInterval?: number;
  damageMultiplier?: number;
  stunDuration?: number;
  stunChance?: number;
  auraDamage?: number;
  auraRadius?: number;
  // CR-H8 (markedDotBoost):
  tickMultiplier?: number;
  durationMultiplier?: number;
  // S37c (chargeReduction / designated):
  chargeMultiplierForMarked?: number;
  prioritizeMarked?: boolean;
  // S37c (chainOnKill / railarc):
  arcTargets?: number;
  arcDamageDecay?: number;
  arcRadius?: number;
  // B2 dualDot (wildfire):
  applyFireDot?: boolean;
  fireDotTickDamage?: number;
  fireDotDuration?: number;
  // B2 shared spread boosts (wildfire + waterborne):
  spreadChanceBonus?: number;
  // B2 enhancedSpread (waterborne):
  spreadRadiusMultiplier?: number;
  maxGenerationsBonus?: number;
  // B2 unlimitedSpreadOnMarked (plague):
  markedIgnoreGenerationCap?: boolean;
  markedSpreadChance?: number;
}

export interface VirusSynergyMods {
  fireDot: { tickDamage: number; duration: number } | null;
  spreadChanceBonus: number;
  spreadRadiusMultiplier: number;
  maxGenerationsBonus: number;
  markedIgnoreGenerationCap: boolean;
  markedSpreadChance: number;
}

export interface SynergyDefinition {
  id: string;
  name: string;
  elements: Element[];
  description: string;
  color: string;
  colorLight: string;
  effect: SynergyEffect;
}

export interface SynergyDataFile {
  synergies: SynergyDefinition[];
}

export interface CardEffect {
  target: string;
  type: string;
  value?: number | boolean;
  element?: Element;
  duration?: number;
  fireRateMultiplier?: number;
  rangeMultiplier?: number;
  bossValue?: number;
  /** Trade-off / downside magnitude (e.g. damage penalty, lives cost). */
  penalty?: number;
  /** Conditional threshold (e.g. lives threshold for DEAD_MAN'S_SWITCH). */
  threshold?: number;
}

export interface Card {
  id: string;
  name: string;
  description: string;
  headline?: string;
  category: "offensive" | "defensive" | "utility" | "legendary";
  rarity: "common" | "rare" | "epic" | "legendary";
  weight: number;
  minWave: number;
  duration?: number;
  effect: CardEffect;
  _selectedAtWave?: number;
}

/** D-322 / AC-5 (Task-007) — per-epoch draft rarity-weight multipliers
 *  (`DraftSystem.RARITY_EPOCH_I/II/III`). Rarity id → multiplier. */
export type RarityEpochWeights = Record<string, number>;

export interface CardDataFile {
  maxSlots: number;
  skipReward: number;
  cards: Card[];
  /** D-322 / AC-5 (Task-007) — optional override for the three wave-epoch
   *  rarity-weight tables. Absent/partial epoch → the byte-safe code default
   *  for that epoch (`DraftSystem.ts`). */
  rarityEpochs?: {
    I?: RarityEpochWeights;
    II?: RarityEpochWeights;
    III?: RarityEpochWeights;
  };
}

// ─── Active Synergy (runtime) ──────────────────────────
export interface ActiveSynergy extends SynergyDefinition {
  towers: {
    col: number;
    row: number;
    x: number;
    y: number;
    element: Element;
  }[];
  centerX: number;
  centerY: number;
  _timer?: number;
}
